import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  type ElementRef,
  HostListener,
  inject,
  type OnInit,
  PLATFORM_ID,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { environment } from '../../../../environments/environment';
import { isPlatformBrowser } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { filter, distinctUntilChanged, type Observable, of } from 'rxjs';
import { CmsService } from '../../services/cms.service';
import { Nav } from '../../enums/nav.enum';
import { UtilService } from '../../services/util.service';
import { StorageService } from '../../services/storage.service';
import { StorageEnum } from '../../enums/storage.enum';
import { AccountDetail } from '../../models/account-detail';
import { ChatbotService } from '../../services/chatbot.service';
import type { ChatBotLabels, ChatBotPageMessage } from '../../models/chat-bot';

/**
 * Chat bot component that provides an embedded chat interface.
 * Displays a floating chat button and toast notifications to engage users.
 */
@Component({
  selector: 'app-chat-bot',
  imports: [],
  templateUrl: './chat-bot.component.html',
  styleUrl: './chat-bot.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatBotComponent implements OnInit {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly router = inject(Router);
  private readonly cms = inject(CmsService);
  private readonly utils = inject(UtilService);
  private readonly storage = inject(StorageService);
  private readonly chatbot = inject(ChatbotService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly browser = isPlatformBrowser(this.platformId);
  private intervalId: ReturnType<typeof setInterval> | null = null;

  @ViewChild('form') postForm: ElementRef | undefined;

  /** Whether the chat window is currently visible */
  readonly showWindow = signal(false);
  /** Whether the toast notification is visible */
  readonly showToastMessage = signal(false);
  /** Current toast message text */
  readonly toastMessage = signal('');
  /** Whether the page content is ready to display */
  readonly pageReady = signal(false);

  readonly reqURL = environment.chatBotURL;
  readonly enableChatbot = environment.enableChatbot;
  readonly pageModel = 'oas-chatbot';

  chatBotSessionId = '';
  chatBotOASAccountId = '';
  chatBotOASUserName = '';
  pageLabels: ChatBotLabels | null = null;
  userName = '';
  accountId = '';
  accountDetail: AccountDetail = new AccountDetail();

  private timerCount = 0;

  /** Computed signal indicating if page is currently loading */
  readonly isPageLoading = computed(() => this.utils.isSpinnerOnPage());

  /** Initializes the chat bot component and sets up event listeners */
  ngOnInit(): void {
    this.cms
      .getContent(this.pageModel, 'Chat Bot')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data: ChatBotLabels) => {
        this.pageLabels = data;

        if (!this.utils.isNullOrUndefined(this.pageLabels)) {
          this.pageReady.set(true);
        }
        if (this.browser) {
          this.checkForCookies();
          this.router.events
            .pipe(
              filter((event): event is NavigationEnd => event instanceof NavigationEnd),
              distinctUntilChanged(),
              takeUntilDestroyed(this.destroyRef),
            )
            .subscribe((event: NavigationEnd) => {
              this.getToastMessage(event.urlAfterRedirects);
              this.timerCount = 0;
              this.showToastMessage.set(false);
              if (event.url === Nav.Outage) {
                this.pageReady.set(false);
              }
            });
          this.chatbotTimer();
          if (!this.utils.isNullOrUndefined(this.pageLabels)) {
            this.getToastMessage(window.location.href);
          }
        }
      });
  }

  @HostListener('document:mousemove')
  onMouseOver(): void {
    this.timerCount = 0;
  }

  @HostListener('window:keydown')
  onKeyDown(): void {
    this.timerCount = 0;
  }

  /**
   * Toggles the chat iframe visibility and initializes cookies when opening.
   * @param event - The triggering click or keyboard event
   */
  showIframe(event: Event): void {
    event.preventDefault();
    const isOpening = !this.showWindow();
    this.showWindow.set(isOpening);
    this.timerCount = 0;
    this.showToastMessage.set(false);
    if (isOpening) {
      this.checkForCookies().subscribe((res) => {
        if (res === 'cookies created') {
          setTimeout(() => {
            this.postForm?.nativeElement.submit();
          }, 50);
        }
      });
    }
  }

  checkForCookies(): Observable<any> {
    // check for each of three cookies and pass those values in form if they exist, otherwise pass empty values
    this.getAccountInfo();

    if (document.cookie.includes('chatBotSessionId')) {
      this.chatBotSessionId = this.chatbot.getCookie('chatBotSessionId');
    } else {
      // create session GUID if chatbot cookie doesn't exist
      this.createCookieSession();
    }

    this.chatBotOASAccountId = this.accountId;
    if (this.accountId !== '') {
      this.chatbot.setCookie('chatBotOASAccountId', this.accountId);
    }
    this.chatBotOASUserName = this.userName;
    if (this.userName !== '') {
      this.chatbot.setCookie('chatBotOASUserName', this.userName);
    }
    return of('cookies created');
  }

  createCookieSession(): void {
    const sessionGUID = this.generateGuid();
    this.chatbot.setCookie('chatBotSessionId', sessionGUID);
    this.chatBotSessionId = sessionGUID;
  }

  /** Closes the toast notification message */
  closeToastMessage(): void {
    this.showToastMessage.set(false);
  }

  /**
   * Sets the appropriate toast message based on the current URL.
   * @param currentURL - The current page URL to match against
   */
  private getToastMessage(currentURL: string): void {
    if (!this.pageLabels?.chatBotMessages?.[0]) {
      return;
    }

    const pageMessageMap: Record<string, string> = {
      [Nav.AccountSummary]: 'Account Summary',
      [Nav.Wallet]: 'Wallet',
      [Nav.ManageAccount]: 'Manage Account',
      [Nav.Payment]: 'Payment',
    };

    const pageName = pageMessageMap[currentURL];
    const messageConfig = this.pageLabels.chatBotMessages[0];

    if (pageName) {
      const pageMessage = messageConfig.messageForPage.find((el: ChatBotPageMessage) => el.page === pageName);
      this.toastMessage.set(pageMessage?.message ?? messageConfig.defaultMessage);
    } else {
      this.toastMessage.set(messageConfig.defaultMessage);
    }
  }

  /**
   * Starts the chatbot timer that controls toast message visibility.
   * Shows toast after 30 seconds of inactivity, hides after 45 seconds.
   */
  private chatbotTimer(): void {
    this.intervalId = setInterval(() => {
      this.timerCount = this.timerCount + 1;
      if (this.timerCount > 29) {
        this.showToastMessage.set(true);
      }
      if (this.timerCount > 44) {
        this.showToastMessage.set(false);
      }
    }, 1000);

    this.destroyRef.onDestroy(() => {
      if (this.intervalId) {
        clearInterval(this.intervalId);
        this.intervalId = null;
      }
    });
  }

  /**
   * Generates a UUID v4 string for session identification.
   * @returns A randomly generated UUID string
   */
  private generateGuid(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  /** Retrieves account information from session storage */
  private getAccountInfo(): void {
    this.accountDetail = this.storage.getSession(StorageEnum.AccountDetail);
    if (!this.utils.isNullOrUndefined(this.accountDetail)) {
      this.accountId = this.accountDetail.AccountNumber ?? '';
      this.userName = this.accountDetail.FirstName ?? '';
    }
  }
}
